import { useMemo, useState } from 'react';
import { Box, Typography, Avatar, useTheme, Tooltip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { BiUserX } from 'react-icons/bi';
import { MdSchedule, MdEventBusy, MdHelpOutline, MdMoreVert, MdPerson, MdAccessTime } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { ScheduleTimelineRow } from '../hooks/useScheduleData';
import { useDriverSchedule } from '@features/drivers/hooks/useDriverSchedule';
import { processDriverSchedule } from '@features/drivers/utils/driverScheduleUtils';
import { calculateUnavailableBlocks } from '@hopdrive/sdk-appointments';
import { ProcessedDriverSchedule } from '@features/drivers/utils/driverScheduleUtils';

type ScheduleStatus = 'set-available' | 'set-unavailable' | 'not-set';

const getDriverScheduleStatus = (
  driverSchedule: ProcessedDriverSchedule[] | undefined,
  selectedDate: string,
  timezone: string
): ScheduleStatus => {
  if (!driverSchedule || driverSchedule.length === 0) {
    return 'not-set';
  }

  const unavailableBlocks = calculateUnavailableBlocks(driverSchedule, selectedDate, timezone);

  // If the entire day is unavailable due to 'not-scheduled', driver hasn't set schedule
  if (unavailableBlocks.some(block => block.reason === 'not-scheduled')) {
    return 'not-set';
  }

  // Check if the ENTIRE day is covered by a single unavailable block (full day off)
  // A driver with working hours (e.g., 9am-5pm) will have off-shift blocks for times
  // outside those hours, but that doesn't mean they're unavailable for the day
  if (unavailableBlocks.length === 1) {
    const block = unavailableBlocks[0];
    const blockStart = new Date(block.startTime);
    const blockEnd = new Date(block.endTime);
    const durationHours = (blockEnd.getTime() - blockStart.getTime()) / (1000 * 60 * 60);

    // If the single unavailable block spans ~24 hours, the driver is off for the entire day
    if (durationHours >= 23.5 && (block.reason === 'off-shift' || block.reason === 'one-off')) {
      return 'set-unavailable';
    }
  }

  // Driver has a schedule set and has working hours on this day
  return 'set-available';
};

interface ScheduleIndexRowProps {
  dataItem: ScheduleTimelineRow;
  index: number;
  isLastItem: boolean;
  rowSize: number;
  isSelected?: boolean;
  onClick?: () => void;
  isLite?: boolean;
  selectedDate?: string;
  timezone?: string;
}

const ScheduleIndexRow = ({
  dataItem,
  rowSize,
  isSelected,
  onClick,
  isLite,
  selectedDate,
  timezone,
}: ScheduleIndexRowProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { driver, scenarios, offeredScenarios } = dataItem;
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  // Determine display info
  const displayName = driver ? driver.user?.display_name || 'Unknown Driver' : 'Unassigned';
  const avatarUrl = driver?.user?.avatar_url;
  const isUnassigned = !driver;
  const scenarioCount = scenarios.length;
  const offeredScenarioCount = offeredScenarios?.length || 0;

  const driverId = driver?.id;

  // Only fetch schedule for Lite tier and real drivers
  const { driverSchedule } = useDriverSchedule(
    isLite && driverId ? String(driverId) : undefined
  );

  // Determine schedule status for the icon
  const scheduleStatus = useMemo((): ScheduleStatus | null => {
    if (!isLite || !driver || !selectedDate || !timezone || !driverId) {
      return null;
    }

    const processed = processDriverSchedule(driverSchedule, String(driverId));
    return getDriverScheduleStatus(processed, selectedDate, timezone);
  }, [isLite, driver, driverSchedule, driverId, selectedDate, timezone]);

  // Status indicator color
  const getStatusColor = () => {
    if (isUnassigned) return theme.palette.utility.main;
    if (driver.status === 'online') return theme.palette.success.main;
    if (driver.status === 'offline') return theme.palette.grey[500];
    return theme.palette.utility.main;
  };

  // Schedule indicator icon component
  const ScheduleIndicator = () => {
    if (!scheduleStatus) return null;

    switch (scheduleStatus) {
      case 'set-available':
        return (
          <Tooltip title="Schedule set - Available" arrow>
            <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
              <MdSchedule size={14} color={theme.palette.success.main} />
            </Box>
          </Tooltip>
        );
      case 'set-unavailable':
        return (
          <Tooltip title="Schedule set - Off duty" arrow>
            <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
              <MdEventBusy size={14} color={theme.palette.warning.main} />
            </Box>
          </Tooltip>
        );
      case 'not-set':
        return (
          <Tooltip title="No schedule set" arrow>
            <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
              <MdHelpOutline size={14} color={theme.palette.grey[500]} />
            </Box>
          </Tooltip>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      onClick={onClick}
      sx={{
        height: rowSize,
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        gap: 2,
        backgroundColor: isSelected ? theme.palette.action.selected : 'transparent',
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background-color 0.2s ease',
        '&:hover': onClick
          ? {
              backgroundColor: isSelected
                ? theme.palette.action.selected
                : theme.palette.action.hover,
            }
          : {},
      }}
    >
      {/* Avatar or Icon */}
      {isUnassigned ? (
        <Avatar
          sx={{
            bgcolor: theme.palette.utility.lighter,
            color: theme.palette.utility.darker,
            width: 40,
            height: 40,
          }}
        >
          <BiUserX size={20} />
        </Avatar>
      ) : (
        <Avatar
          src={avatarUrl || undefined}
          sx={{
            width: 40,
            height: 40,
            bgcolor: theme.palette.primary.main,
          }}
        >
          {displayName.charAt(0).toUpperCase()}
        </Avatar>
      )}

      {/* Driver Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {displayName}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Status indicator */}
          {!isUnassigned && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: getStatusColor(),
              }}
            />
          )}
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
            }}
          >
            {scenarioCount + offeredScenarioCount} {scenarioCount + offeredScenarioCount === 1 ? 'move' : 'moves'}
          </Typography>
          {/* Schedule indicator (Lite tier only) */}
          <ScheduleIndicator />
        </Box>
      </Box>

      {/* Action menu (Lite tier real drivers only) */}
      {isLite && driver && (
        <>
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
            }}
            sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
          >
            <MdMoreVert size={18} />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={() => setMenuAnchor(null)}
            onClick={e => e.stopPropagation()}
          >
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                navigate(`/drivers/${driver.id}`);
              }}
            >
              <ListItemIcon><MdPerson size={18} /></ListItemIcon>
              <ListItemText>View Profile</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                navigate(`/drivers/${driver.id}?tab=availability`);
              }}
            >
              <ListItemIcon><MdAccessTime size={18} /></ListItemIcon>
              <ListItemText>Availability</ListItemText>
            </MenuItem>
          </Menu>
        </>
      )}
    </Box>
  );
};

export default ScheduleIndexRow;
